import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ReactiveFormsModule, FormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ButtonComponent } from '@ppl/ui';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NgZorroModule } from 'src/app/shared/modules/ng-zorro/ng-zorro.module';
import { DatePickerModule } from 'primeng/datepicker';
import { UserService } from 'src/app/services/user.service';
import { TimesheetService } from 'src/app/services/timesheet.service';
import { DateTimeFormatterService } from 'src/app/shared/utils/date-time-formatter.service';
import { ErrorAlertService } from 'src/app/shared/services/error-alert.service';

@Component({
    imports: [
        CommonModule,
        NgZorroModule,
        ButtonComponent,
        ReactiveFormsModule,
        NzDatePickerModule,
        FormsModule,
        DatePickerModule
    ],
    selector: 'ppl-add-sick-time',
    templateUrl: './add-sick-time.component.html',
    styleUrls: ['./add-sick-time.component.css']
})
export class AddSickTimeComponent implements OnInit {
    @Input() consumers: any;
    @Input() providerId: any;
    @Input() programName: string = '';
    @Output() timeOffAdded = new EventEmitter<any>();

    applySickLeaveForm: any;
    timeInTimeOutEqualMessage = 'Service Time In cannot be equal to Service Time Out.';
    timeOutLessThanTimeInMessage = 'Service Time In cannot be greater than Service Time Out.';
    timeOutOverNightShiftMessage = 'Overnight shifts must be entered separately after midnight (12:00 AM).';
    serviceCodesTO: any[] = [];
    isConsentRequired: boolean = false;
    error: boolean = false;
    fieldErrors: any = {};
    isAgreed: boolean = false;
    payPeriod: any[] = [];
    saveTimeOffErrorMessage: string | null = null;

    constructor(private fb: FormBuilder,
        private userService: UserService,
        private timesheetService: TimesheetService,
        private dateFormatService: DateTimeFormatterService,
        private errorAlertService: ErrorAlertService) { }

    ngOnInit(): void {

        this.applySickLeaveForm = this.fb.group(
            {
                consumer: [null, Validators.required],
                serviceDate: [null, Validators.required],
                timeSheetDetailId: [null],
                service: [null, Validators.required],
                serviceTimeIn: [null, Validators.required],
                serviceTimeOut: [null, Validators.required],
                totalTime: [{ value: null, disabled: true }],
                billedUnits: [{ value: null, disabled: true }],
                isAgreed: [false, Validators.requiredTrue]
            }
        );

        this.applySickLeaveForm.get('serviceTimeIn')?.valueChanges.subscribe((val: any) => {
            const coerced = this.coerceToDate(val);
            if (coerced && coerced !== val) {
                this.applySickLeaveForm.get('serviceTimeIn')?.setValue(coerced, { emitEvent: false });
            }
            this.updateTimesheetUnits();
        });

        this.applySickLeaveForm.get('serviceTimeOut')?.valueChanges.subscribe((val: any) => {
            const coerced = this.coerceToDate(val);
            if (coerced && coerced !== val) {
                this.applySickLeaveForm.get('serviceTimeOut')?.setValue(coerced, { emitEvent: false });
            }
            this.updateTimesheetUnits();
        });

        // ensure any pre-populated string values are coerced on init
        const initIn = this.coerceToDate(this.applySickLeaveForm.get('serviceTimeIn')?.value);
        if (initIn && initIn !== this.applySickLeaveForm.get('serviceTimeIn')?.value) {
            this.applySickLeaveForm.get('serviceTimeIn')?.setValue(initIn, { emitEvent: false });
        }
        const initOut = this.coerceToDate(this.applySickLeaveForm.get('serviceTimeOut')?.value);
        if (initOut && initOut !== this.applySickLeaveForm.get('serviceTimeOut')?.value) {
            this.applySickLeaveForm.get('serviceTimeOut')?.setValue(initOut, { emitEvent: false });
        }

        const selectedProgram = this.userService.getSelectedProgram();
        const userDetails = this.userService.getUserDetails();

        if (!userDetails) {
            console.error('no user details found');
            return;
        }

        const isHRStaff = selectedProgram?.roles.some((role: any) => role.role_name === 'PPL HR Staff') && userDetails.usertype_lkp === 'FMS';

        this.applySickLeaveForm.get('consumer')?.valueChanges.subscribe((consumerId: any) => {
            this.applySickLeaveForm.get('service')?.setValue(null);
            this.serviceCodesTO = [];
            if (consumerId) {
                this.fetchTimeoffServiceCodes(consumerId);
            }
        });

        this.getPayPeriod();
    }

    fetchTimeoffServiceCodes(consumerId: any) {
        this.timesheetService
            .getTimeoffServiceCodesByConsumer(this.programName, consumerId, this.providerId)
            .subscribe((data: any) => {
                this.serviceCodesTO = data?.responsedata || [];
            });
    }

    saveTimeOff() {
        this.saveTimeOffErrorMessage = null;
        const payload = this.createTimeOffPayload();
        let isValid = true;
        this.fieldErrors = {};

        this.isConsentRequired = false;

        if (!this.applySickLeaveForm.get('isAgreed')?.value) {
            isValid = false;
            this.isConsentRequired = true;
            const errorMessage = 'Please select the consent to save the time off.';
            this.errorAlertService.openModal('error', 'Error', errorMessage);
            return;
        }

        if (payload.timesheetLines.length == 0) {
            const errorMessage = 'Please select the time off to save.';
            this.errorAlertService.openModal('error', 'Error', errorMessage);
            return;
        }

        const service = this.applySickLeaveForm.get('service')?.value;
        const timeIn = this.applySickLeaveForm.get('serviceTimeIn')?.value;
        const timeOut = this.applySickLeaveForm.get('serviceTimeOut')?.value;
        const billedUnits = this.applySickLeaveForm.get('billedUnits')?.value;
        const fields = [
            { name: 'service', value: service },
            { name: 'serviceTimeIn', value: timeIn },
            { name: 'serviceTimeOut', value: timeOut },
            { name: 'billedUnits', value: billedUnits },
        ];

        const filledFields = fields.filter(
            (field) => field.value != null && field.value !== ''
        );
        if (!this.fieldErrors) {
            this.fieldErrors = {};
        }

        fields.forEach((field) => {
            if (
                (!field.value && filledFields.length > 0) ||
                (field.name === 'billedUnits' && field.value < 0)
            ) {
                isValid = false;
                this.fieldErrors[field.name] = true;
            } else {
                if (this.fieldErrors[field.name]) {
                    delete this.fieldErrors[field.name];
                }
            }
        });

        if (!isValid) {
            this.error = true;
        } else {
            this.error = false;
            this.timesheetService.saveTimeOff(payload).subscribe((data: any) => {
                if (data.status === 400) {
                    const response = data.responsedata || {};
                    const errors = [];
                    if (response && Array.isArray(response)) {
                        response.forEach((item: any) => {
                            if (item.status === 'FAILURE') {
                                errors.push(item.error);
                            }
                        });

                        if (errors.length) {
                            this.saveTimeOffErrorMessage = 'An employer must be assigned before you can submit timesheets or time-off requests.';
                        }
                    }
                }

                if (data.status === 200) {
                    this.timeOffAdded.emit({ timeOffAdded: true });
                }
            });
        }
    }

    updateTimesheetUnits(): void {
        const serviceTimeIn = this.applySickLeaveForm.get('serviceTimeIn')?.value;
        const serviceTimeOut = this.applySickLeaveForm.get('serviceTimeOut')?.value;
        const serviceCode = this.applySickLeaveForm.get('service')?.value;

        if (!serviceCode || !serviceTimeIn || !serviceTimeOut) {
            this.applySickLeaveForm.get('billedUnits')?.setValue(null);
            this.applySickLeaveForm.get('totalTime')?.setValue(null);
            return;
        }

        const totalMinutesIn = serviceTimeIn?.getHours() * 60 + serviceTimeIn?.getMinutes();
        const totalMinutesOut = serviceTimeOut?.getHours() * 60 + serviceTimeOut?.getMinutes();

        delete this.fieldErrors['timeInEqualTimeOut'];
        delete this.fieldErrors['timeInAfterTimeOut'];
        delete this.fieldErrors['timeInAfterTimeOutOverNightShift'];

        if (totalMinutesIn === totalMinutesOut) {
            this.fieldErrors['timeInEqualTimeOut'] = true;
            this.applySickLeaveForm.get('billedUnits')?.setValue(null);
            this.applySickLeaveForm.get('totalTime')?.setValue(null);
            return;
        }

        if (totalMinutesOut < totalMinutesIn) {
            const outHours = serviceTimeOut.getHours();
            const outMinutes = serviceTimeOut.getMinutes();
            this.fieldErrors['timeInAfterTimeOut'] = true;
            this.applySickLeaveForm.get('billedUnits')?.setValue(null);
            this.applySickLeaveForm.get('totalTime')?.setValue(null);
            return;
        }

        // Normalize both times to the same date to avoid date-portion mismatch
        const normalizedIn = new Date(0, 0, 0, serviceTimeIn.getHours(), serviceTimeIn.getMinutes(), 0);
        const normalizedOut = new Date(0, 0, 0, serviceTimeOut.getHours(), serviceTimeOut.getMinutes(), 0);

        // Calculate duration and billed units
        const totalTimeValue = this.dateFormatService.getDuration(normalizedIn, normalizedOut);
        this.applySickLeaveForm.get('totalTime')?.setValue(totalTimeValue);
        this.applySickLeaveForm.get('billedUnits')?.setValue(totalTimeValue);
    }

    createTimeOffPayload() {
        // const formData = this.applySickLeaveForm.getRawValue();
        // const payroll = this.payPeriod.filter(
        //     (period: any) => period.payrollId === formData.period
        // );
        // const [startDate, endDate] = payroll[0].payPeriod.split('-');
        return {
            programCode: this.programName,
            provider: {
                providerId: this.userService.getSelectedLead()!,
            },
            consumer: {
                consumerId: this.applySickLeaveForm.get('consumer')?.value,
            },
            payrollScheduleId: this.findPayrollIdByDate(this.applySickLeaveForm.get('serviceDate')?.value),
            timesheetLines: this.translateTimeOffRows(),
        };
    }

    private coerceToDate(val: any): Date | null {
        if (!val) return null;
        if (val instanceof Date) return new Date(val.getTime());
        if (typeof val === 'number') {
            const d = new Date(val);
            return isNaN(d.getTime()) ? null : d;
        }
        if (typeof val === 'string') {
            const dIso = new Date(val);
            if (!isNaN(dIso.getTime())) return dIso;
            const m = val.match(/(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)?/i);
            if (m) {
                let hours = parseInt(m[1], 10);
                const minutes = parseInt(m[2], 10);
                const seconds = m[3] ? parseInt(m[3], 10) : 0;
                const ampm = m[4];
                if (ampm) {
                    if (ampm.toUpperCase() === 'PM' && hours < 12) hours += 12;
                    if (ampm.toUpperCase() === 'AM' && hours === 12) hours = 0;
                }
                const d = new Date();
                d.setHours(hours, minutes, seconds, 0);
                return d;
            }
        }
        return null;
    }

    translateTimeOffRows() {
        let timesheetLines: any[] = [];

        if (this.applySickLeaveForm.get('serviceTimeIn')?.value && this.applySickLeaveForm.get('serviceTimeOut')?.value) {
            const selectedService = this.applySickLeaveForm.get('service')?.value;
            let serviceCodeLkp = selectedService?.serviceCodeLkp ?? selectedService?.code ?? null;
            let serviceCodeDetailId = selectedService?.serviceCodeDetailId ?? null;

            // If either value is missing try to find the matching entry in serviceCodesTO
            if ((!serviceCodeLkp || !serviceCodeDetailId) && this.serviceCodesTO && this.serviceCodesTO.length) {
                const found = this.serviceCodesTO.find((sc: any) =>
                    sc?.serviceCodeDetailId === selectedService
                );
                if (found) {
                    serviceCodeLkp = found.serviceCodeLkp;
                    serviceCodeDetailId = found.serviceCodeDetailId;
                }
            }

            timesheetLines.push({
                serviceDate: this.dateFormatService.formatDate(this.applySickLeaveForm.get('serviceDate')?.value, 'MM/dd/yyyy') || '',
                serviceTimeIn: this.formatTime(new Date(this.applySickLeaveForm.get('serviceTimeIn')?.value)),
                serviceTimeOut: this.formatTime(new Date(this.applySickLeaveForm.get('serviceTimeOut')?.value)),
                serviceCodeLkp: serviceCodeLkp,
                serviceCodeDetailId: serviceCodeDetailId,
            });
        }

        return timesheetLines;
    }

    private findPayrollIdByDate(date: Date | null): number | null {
        if (!date || !this.payPeriod || !this.payPeriod.length) {
            return null;
        }
        const d = new Date(date);
        const target = new Date(d.getFullYear(), d.getMonth(), d.getDate());
        for (const p of this.payPeriod) {
            const start = new Date(p.payrollStartDate);
            const end = new Date(p.payrollEndDate);
            const startDate = new Date(start.getFullYear(), start.getMonth(), start.getDate());
            const endDate = new Date(end.getFullYear(), end.getMonth(), end.getDate());
            if (target >= startDate && target <= endDate) {
                return p.payrollId;
            }
        }
        return null;
    }

    isFieldEmpty(fieldName: string): boolean {
        return this.fieldErrors && this.fieldErrors[fieldName];
    }

    isBilledUnitsNegative(): boolean {
        const billedUnits = this.applySickLeaveForm.get('billedUnits')?.value;
        return billedUnits < 0;
    }

    isFormValid(): boolean {
        return this.applySickLeaveForm.valid && !this.isBilledUnitsNegative() && Object.keys(this.fieldErrors).length === 0;
    }

    formatTime(date: Date): string {
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = '00';

        return `${hours}:${minutes}:${seconds}`;
    }

    onTimePickerShow(controlName: string): void {
        const ctrl = this.applySickLeaveForm.get(controlName);
        const val = ctrl?.value;
        if (!val || !(val instanceof Date)) {
            // Build a date object; prefer the selected service date if available
            const d = new Date();
            const sd = this.applySickLeaveForm.get('serviceDate')?.value;
            if (sd) {
                const sDate = new Date(sd);
                d.setFullYear(sDate.getFullYear(), sDate.getMonth(), sDate.getDate());
            }
            // keep existing time if it can be coerced
            const coerced = this.coerceToDate(val);
            if (coerced) {
                d.setHours(coerced.getHours(), coerced.getMinutes(), coerced.getSeconds(), 0);
            }
            ctrl?.setValue(d);
        }
    }

    getPayPeriod() {
        this.timesheetService
            .getPayPeriod(this.programName)
            .subscribe((data: any) => {
                this.payPeriod = data.responsedata;
            });
    }
}